import type { AccessLevel, MeResponse, PageArea } from '../models/extranet.model';

/*
 * Eager on purpose: the shell's menu, `IdentityService` and `areaGuard` read it on the first page,
 * so it imports only types — a runtime import of extranet.model.ts (or of `access.ts`, which has
 * one) would bring the whole model's runtime code into the initial bundle (Feature 4123).
 */

/**
 * The caller's level on a page area (Feature 4123), from GET /extranet/access/me:
 *
 * - a platform admin outside view-as (`role` ADMIN, `profiles: []`, `permissions: {}`) is not
 *   restricted: EDIT everywhere — the admin pages keep their own `adminGuard`;
 * - anyone else gets `permissions[area]`; an area missing from the map is NONE. Under view-as and
 *   for a legacy hotel account the BE already answers `[OWNER]` with every area at EDIT;
 * - an identity not resolved yet (`null`) is NONE: nothing is granted before the BE answered
 *   (the shell's menu does not filter until then, see `AppComponent.navItems`).
 */
export function levelOf(me: MeResponse | null | undefined, area: PageArea): AccessLevel {
  if (!me) return 'NONE';
  if (me.role === 'ADMIN') return 'EDIT';
  return me.permissions?.[area] ?? 'NONE';
}

/**
 * "Can manage the team" (Feature 4123): the Owner of a group — invites members and changes their
 * profiles (Settings → Team, the account menu's "Invite a teammate", `ownerGuard`). Both are needed:
 * a legacy hotel account is answered `[OWNER]` but has no group, so it has no team to manage (the
 * BE refuses its team calls with OWNER_REQUIRED). Under view-as the BE answers `[OWNER]` with the
 * viewed group's id, so the admin manages that group's team.
 */
export function isTeamManager(me: MeResponse | null | undefined): boolean {
  return !!me?.groupId && (me.profiles ?? []).includes('OWNER');
}
